import type { ReactNode } from 'react';
import { LinkSimpleHorizontalIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';

/**
 * The Calc or Qty header while both are drawn. Qty overrides Calc, so the two
 * columns are pinned together: one rule runs under both headers, and a link
 * mark at the end of Calc says why on hover. The header cell is `relative`
 * (the table is resizable), which anchors the rule to its bottom edge.
 */
export function PairedQuantityHeader({
  side,
  children,
}: {
  side: 'calc' | 'quantity';
  children: ReactNode;
}) {
  const gt = useGT();
  const linkLabel = gt(
    'Qty overrides Calc, so the two columns stay together.',
    {
      context:
        'tooltip on the link mark between the Calc and Qty column headers of the buyer board',
    },
  );
  return (
    <>
      <div className="flex items-center justify-end gap-0.5">
        {children}
        {side === 'calc' && (
          <span role="img" aria-label={linkLabel} title={linkLabel}>
            <LinkSimpleHorizontalIcon
              aria-hidden
              className="size-3.5 shrink-0 text-sky-600"
            />
          </span>
        )}
      </div>
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute bottom-0 h-0.5 bg-sky-500/70',
          side === 'calc'
            ? 'left-2 right-0 rounded-l-full'
            : 'left-0 right-2 rounded-r-full',
        )}
      />
    </>
  );
}
